import React, { useEffect, useState, useMemo } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchAttendanceAdmin } from "../../action/AttendanceAdminAction";

const BRAND = "#330066";

const Page = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: "0 0 80px",
}));

const Card = styled(Box)({
  background: "#fff",
  border: "0.5px solid #e2e8f0",
  borderRadius: 12,
  overflow: "hidden",
});

const SectionLabel = styled(Typography)({
  fontSize: 11,
  fontWeight: 600,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  marginBottom: 6,
  marginTop: 14,
});

const formatDate = (d) => new Date(d).toISOString().split("T")[0];
const formatTime = (d) =>
  new Date(d).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const sortAttendance = (arr) =>
  [...arr].sort((a, b) => {
    if (!a.inTime && b.inTime) return 1;
    if (a.inTime && !b.inTime) return -1;
    if (!a.inTime && !b.inTime)
      return (a.designation || "").localeCompare(b.designation || "");
    const tc = (a.inTime || "").localeCompare(b.inTime || "");
    return tc !== 0 ? tc : (a.designation || "").localeCompare(b.designation || "");
  });

export default function AttendanceAdmin() {
  const dispatch = useDispatch();
  const { loading, data } = useSelector((s) => s.attendanceAdmin);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    dispatch(fetchAttendanceAdmin(formatDate(selectedDate)));
  }, [dispatch, selectedDate]);

  const sortedData = useMemo(() => (data ? sortAttendance(data) : []), [data]);

  const totalEmployees = data?.length || 0;
  const availableCount = data?.filter((i) => i.inTime).length || 0;
  const absentCount = totalEmployees - availableCount;
  const availablePercentage =
    totalEmployees > 0 ? ((availableCount / totalEmployees) * 100).toFixed(1) : "0.0";

  const rateColor =
    Number(availablePercentage) >= 80
      ? "#16a34a"
      : Number(availablePercentage) >= 60
      ? "#d97706"
      : "#dc2626";

  const handleExportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const at = new Date();
    const pw = doc.internal.pageSize.getWidth();

    doc.setFontSize(22).setFont("helvetica", "bold").setTextColor(30, 58, 110);
    doc.text("DTS Biztrack", pw / 2, 20, { align: "center" });
    doc.setFontSize(13).setTextColor(100, 116, 139);
    doc.text("Admin Attendance Report", pw / 2, 30, { align: "center" });
    doc.setDrawColor(30, 58, 110).setLineWidth(0.5).line(15, 36, pw - 15, 36);

    const y = 44, sx = 15, tw = pw - 30;
    [
      [sx + tw * 0.15, "Date – ",  formatDate(selectedDate)],
      [sx + tw * 0.5,  "Time – ",  formatTime(at)],
      [sx + tw * 0.85, "Total – ", `${totalEmployees} (${availablePercentage}%)`],
    ].forEach(([col, bold, val]) => {
      const fw = doc.getTextWidth(bold + val);
      doc.setFontSize(10).setFont("helvetica", "bold").setTextColor(60, 60, 60);
      doc.text(bold, col - fw / 2, y);
      doc.setFont("helvetica", "normal").text(val, col - fw / 2 + doc.getTextWidth(bold), y);
    });

    autoTable(doc, {
      startY: 52,
      head: [["Name", "Designation", "In Time", "Out Time"]],
      body: sortedData.map((item) => [
        item.name || "",
        item.designation || "",
        item.inTime || "",
        item.outTime
          ? item.outTime
          : item.inTime
          ? "Available"
          : "Absent",
      ]),
      styles: { fontSize: 10, cellPadding: 4 },
      headStyles: { fillColor: [21, 76, 121], textColor: 255, fontStyle: "bold", halign: "center" },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      columnStyles: {
        0: { cellWidth: 55 },
        1: { cellWidth: 55 },
        2: { cellWidth: 30, halign: "center" },
        3: { cellWidth: 35, halign: "center" },
      },
      theme: "grid",
      margin: { left: 15, right: 10 },
      didParseCell(hookData) {
        if (hookData.column.index === 3) {
          const raw = hookData.cell.raw;
          if (raw === "Available") {
            hookData.cell.styles.textColor = [0, 181, 26];
            hookData.cell.styles.fontStyle = "bold";
          } else if (raw === "Absent") {
            hookData.cell.styles.textColor = [211, 47, 47];
            hookData.cell.styles.fontStyle = "bold";
          } else if (raw && hookData.section === "body") {
            hookData.cell.styles.textColor = [211, 47, 47];
            hookData.cell.styles.fontStyle = "bold";
          }
        }
      },
      didDrawPage(data) {
        doc.setFontSize(8).setTextColor(150).setFont("helvetica", "italic");
        doc.text(
          `Generated on ${formatDate(at)} at ${formatTime(at)}`,
          pw / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: "center" }
        );
        doc.text(`Page ${data.pageNumber}`, pw - 20, doc.internal.pageSize.getHeight() - 10);
      },
    });

    doc.save(`Admin_Attendance_${formatDate(selectedDate)}.pdf`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Page>

        {/* ── Filter row ── */}
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
          <Box
            onClick={() => setOpenDatePicker(true)}
            sx={{
              height: 36, borderRadius: "20px", border: "0.5px solid #e2e8f0",
              background: "#fff", display: "flex", alignItems: "center",
              gap: "6px", px: "12px", cursor: "pointer", flexShrink: 0,
              "&:hover": { borderColor: BRAND },
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 14, color: "#64748b" }} />
            <Typography sx={{ fontSize: 12, color: "#1e293b", fontWeight: 500 }}>
              {formatDate(selectedDate)}
            </Typography>
          </Box>

          {openDatePicker && (
            <DatePicker
              open={openDatePicker}
              value={selectedDate}
              onChange={(date) => { setSelectedDate(date); setOpenDatePicker(false); }}
              onClose={() => setOpenDatePicker(false)}
              renderInput={(params) => <TextField {...params} sx={{ display: "none" }} />}
            />
          )}
        </Box>

        {/* ── Stats row ── */}
        {!loading && data?.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            {[
              { label: "Total",   value: totalEmployees,      color: BRAND      },
              { label: "Present", value: availableCount,      color: "#16a34a"  },
              { label: "Absent",  value: absentCount,         color: "#dc2626"  },
              { label: "Att %",    value: `${availablePercentage}%`, color: rateColor },
            ].map((s) => (
              <Box
                key={s.label}
                sx={{
                  flex: 1, background: "#fff", border: "0.5px solid #e2e8f0",
                  borderRadius: "12px", p: "10px 12px",
                }}
              >
                <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>{s.label}</Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 600, color: s.color, lineHeight: 1 }}>{s.value}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* ── PDF button row ── */}
        {!loading && data?.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            <Button
              startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />}
              onClick={handleExportPDF}
              disabled={!data?.length}
              sx={{
                flex: 1, height: 34, borderRadius: "20px",
                background: "#16a34a", color: "#fff", fontSize: 12,
                fontWeight: 500, textTransform: "none",
                "&:hover": { background: "#15803d" },
                "&:disabled": { background: "#cbd5e1", color: "#64748b" },
              }}
            >
              Export PDF
            </Button>
          </Box>
        )}

        {/* ── Table ── */}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} sx={{ color: BRAND }} />
          </Box>
        ) : (
          <Card>
            {/* ── MOBILE layout: Name+Designation stacked, InTime, OutTime ── */}
            {isMobile ? (
              <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ background: BRAND, color: "#fff", fontSize: 11, fontWeight: 600, py: "8px", px: "10px", borderBottom: "none", width: "50%", position: "sticky", top: 0, zIndex: 10 }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ background: BRAND, color: "#fff", fontSize: 11, fontWeight: 600, py: "8px", px: "6px", borderBottom: "none", width: "25%", textAlign: "center", position: "sticky", top: 0, zIndex: 10 }}>
                      In Time
                    </TableCell>
                    <TableCell sx={{ background: BRAND, color: "#fff", fontSize: 11, fontWeight: 600, py: "8px", px: "6px", borderBottom: "none", width: "25%", textAlign: "center", position: "sticky", top: 0, zIndex: 10 }}>
                      Out Time
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.length > 0 ? (
                    sortedData.map((item, i) => {
                      const hasIn = !!item.inTime;
                      const statusLabel = hasIn ? (item.outTime ? item.outTime : "Available") : "Absent";
                      const statusColor = !hasIn ? "#b91c1c" : item.outTime ? "#475569" : "#15803d";
                      const statusBg = !hasIn ? "#fee2e2" : item.outTime ? "#f1f5f9" : "#dcfce7";
                      return (
                        <TableRow
                          key={i}
                          sx={{
                            "&:nth-of-type(even) td": { background: "#f8faff" },
                            "&:hover td": { background: "rgba(21,76,121,0.04)" },
                            "&:last-child td": { borderBottom: "none" },
                          }}
                        >
                          {/* Name + Designation stacked */}
                          <TableCell sx={{ px: "10px", py: "7px" }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3, color: "#1e293b" }}>
                              {item.name}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: "#94a3b8", lineHeight: 1.3, mt: "1px" }}>
                              {item.designation}
                            </Typography>
                          </TableCell>

                          {/* In Time */}
                          <TableCell sx={{ textAlign: "center", px: "4px", py: "7px" }}>
                            {hasIn ? (
                              <Box component="span" sx={{ display: "inline-block", px: "6px", py: "2px", borderRadius: "8px", fontSize: 11, fontWeight: 600, background: "#dcfce7", color: "#15803d" }}>
                                {item.inTime}
                              </Box>
                            ) : (
                              <Box component="span" sx={{ display: "inline-block", px: "6px", py: "2px", borderRadius: "8px", fontSize: 11, background: "#fee2e2", color: "#b91c1c" }}>—</Box>
                            )}
                          </TableCell>

                          {/* Out Time */}
                          <TableCell sx={{ textAlign: "center", px: "4px", py: "7px" }}>
                            <Box component="span" sx={{ display: "inline-block", px: "6px", py: "2px", borderRadius: "8px", fontSize: 11, fontWeight: 600, background: statusBg, color: statusColor }}>
                              {statusLabel}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ textAlign: "center", py: 6, fontSize: 13, color: "#94a3b8" }}>
                        No attendance found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            ) : (
              /* ── DESKTOP layout: all 4 columns ── */
              <TableContainer sx={{ maxHeight: "calc(100vh - 280px)", overflowX: "auto" }}>
                <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%", minWidth: 480 }}>
                  <TableHead>
                    <TableRow>
                      {[
                        { label: "Name",        w: "30%", align: "left"   },
                        { label: "In Time",     w: "17%", align: "center" },
                        { label: "Out Time",    w: "17%", align: "center" },
                        { label: "Designation", w: "36%", align: "left"   },
                      ].map((h) => (
                        <TableCell
                          key={h.label}
                          sx={{
                            width: h.w, background: BRAND, color: "#fff",
                            fontSize: 11, fontWeight: 600, py: "8px", px: "8px",
                            borderBottom: "none", textAlign: h.align,
                            position: "sticky", top: 0, zIndex: 10,
                          }}
                        >
                          {h.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedData.length > 0 ? (
                      sortedData.map((item, i) => {
                        const hasIn = !!item.inTime;
                        const statusLabel = hasIn ? (item.outTime ? item.outTime : "Available") : "Absent";
                        const statusColor = !hasIn ? "#b91c1c" : item.outTime ? "#475569" : "#15803d";
                        const statusBg = !hasIn ? "#fee2e2" : item.outTime ? "#f1f5f9" : "#dcfce7";
                        return (
                          <TableRow
                            key={i}
                            sx={{
                              "&:nth-of-type(even) td": { background: "#f8faff" },
                              "&:hover td": { background: "rgba(21,76,121,0.04)" },
                              "&:last-child td": { borderBottom: "none" },
                            }}
                          >
                            <TableCell sx={{ fontSize: 12, fontWeight: 500, px: "8px", py: "7px", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }}>
                              {item.name}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center", px: "6px", py: "7px" }}>
                              {hasIn ? (
                                <Box component="span" sx={{ display: "inline-block", px: "6px", py: "1px", borderRadius: "8px", fontSize: 11, fontWeight: 600, background: "#dcfce7", color: "#15803d" }}>
                                  {item.inTime}
                                </Box>
                              ) : (
                                <Box component="span" sx={{ display: "inline-block", px: "6px", py: "1px", borderRadius: "8px", fontSize: 11, background: "#fee2e2", color: "#b91c1c" }}>—</Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center", px: "6px", py: "7px" }}>
                              <Box component="span" sx={{ display: "inline-block", px: "6px", py: "1px", borderRadius: "8px", fontSize: 11, fontWeight: 600, background: statusBg, color: statusColor }}>
                                {statusLabel}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: 11, color: "#64748b", px: "8px", py: "7px", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }}>
                              {item.designation}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: "center", py: 6, fontSize: 13, color: "#94a3b8" }}>
                          No attendance found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        )}
      </Page>
    </LocalizationProvider>
  );
}